// 051 (c) Gestación actual -> Observation por dato/grupo bajo el panel `gestacion-actual`.
// Peso inicial, talla, grupo/Rh, Hb y glucemia llevan LOINC (verificados en tx.fhir.org); el resto código propio.
import { leerSiNo } from '../camposSimplesDatos';
import {
  CERVIX_EXAMENES_OPCIONES,
  FACTORES_RIESGO_051,
  SESIONES_EDUCACION_PRENATAL_051,
  TRIMESTRES_051,
  claveFactor051,
  sifilisTieneSemanas051,
  vihTieneResultado051,
  type EstadoGestacionActual051,
  type EstadoHb051,
  type EstadoGlucemia051,
  type EstadoSifilisFila051,
  type EstadoVih051,
} from '../bloques051GestacionDatos';
import { cMarca, cNumero, cOpcion, cSiNo, cFecha, LOINC_051, local, Seccion051, U, valorNumerico, valorOpcion, type Coding, type Contexto051 } from './base';

const P = 'formularios.f051.gestacionActual';
const LOINC_HB: Coding = { system: 'http://loinc.org', code: '718-7', display: 'Hemoglobin [Mass/volume] in Blood' };
const LOINC_GLUCEMIA_AYUNAS: Coding = { system: 'http://loinc.org', code: '1558-6', display: 'Fasting glucose [Mass/volume] in Serum or Plasma' };

export function agregarGestacionActual(ctx: Contexto051, e: EstadoGestacionActual051): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('gestacion-actual', 'Gestación actual'));
  const op = (code: Coding, v: string) => cOpcion(code, v, v ? t(`${P}.opcion.${v}`) : '');
  /** Observation cuyo valor es una elección única (toxoplasmosis, Rh…). */
  const resultado = (codigo: Coding, v: string): void => {
    if (v) s.grupo(codigo, [], { valueCodeableConcept: valorOpcion(v, t(`${P}.opcion.${v}`)) });
  };

  const peso = valorNumerico(e.pesoInicial, U.kg);
  if (peso) s.grupo(LOINC_051.peso, [], peso);
  const talla = valorNumerico(e.talla, U.cm);
  if (talla) s.grupo(LOINC_051.talla, [], talla);

  s.grupo(local('eg-confiable-y-ecografia', 'Edad gestacional confiable y ecografía temprana'), [
    cSiNo(local('eg-confiable-fum', t(`${P}.egConfiableFum`)), e.egConfiableFum),
    cSiNo(local('eg-confiable-eco', t(`${P}.egConfiableEco`)), e.egConfiableEco),
    cSiNo(local('ecografia-11-13-semanas', t(`${P}.ecografia11a13Semanas`)), e.ecografia11a13Semanas),
  ]);

  s.grupo(
    local('factores-riesgo', t(`${P}.factoresTitulo`)),
    FACTORES_RIESGO_051.flatMap((f) => TRIMESTRES_051.map((tri) => cSiNo(local(`${f}-trimestre-${tri}`, `${t(`${P}.factores.${f}`)} — ${t(`${P}.trimestres.${tri}`)}`), leerSiNo(e.factores[claveFactor051(f, tri)]))))
  );

  s.grupo(local('inmunizaciones-y-examenes', 'Inmunizaciones y exámenes preventivos'), [
    op(local('antirrubeola', t(`${P}.antirrubeola`)), e.antirrubeola),
    cSiNo(local('antitetanica-vigente', t(`${P}.antitetanicaVigente`)), e.antitetanicaVigente),
    cFecha(local('antitetanica-dosis-1', t(`${P}.antitetanicaDosis1`)), e.antitetanicaDosis1),
    cFecha(local('antitetanica-dosis-2', t(`${P}.antitetanicaDosis2`)), e.antitetanicaDosis2),
    cSiNo(local('examen-normal-mamas', t(`${P}.examenNormalMamas`)), e.examenNormalMamas),
    cSiNo(local('examen-normal-odontologico', t(`${P}.examenNormalOdontologico`)), e.examenNormalOdontologico),
    ...CERVIX_EXAMENES_OPCIONES.map((c) => op(local(`cervix-${c}`, `${t(`${P}.cervix.titulo`)} — ${t(`${P}.cervix.${c}`)}`), e.cervix[c] ?? '')),
  ]);

  const grupo = e.grupo.trim();
  if (grupo) s.grupo(LOINC_051.grupoAbo, [], { valueCodeableConcept: { text: grupo } });
  s.grupo(LOINC_051.rh, [cSiNo(local('inmunizacion-rh', t(`${P}.inmunizacion`)), e.inmunizacion)], e.rh ? { valueCodeableConcept: valorOpcion(e.rh, t(`${P}.opcion.${e.rh}`)) } : {});

  resultado(local('toxoplasmosis', t(`${P}.toxoplasmosis`)), e.toxoplasmosis);
  resultado(local('chagas', t(`${P}.chagas`)), e.chagas);
  resultado(local('paludismo-malaria', t(`${P}.paludismoMalaria`)), e.paludismoMalaria);
  resultado(local('estreptococo-b', t(`${P}.estreptococoB`)), e.estreptococoB);

  const vih = (clave: 'menor20' | 'mayorIgual20', v: EstadoVih051): void => {
    s.grupo(local(`vih-${clave === 'menor20' ? 'menor-20' : 'mayor-igual-20'}`, `${t(`${P}.vih.titulo`)} ${t(`${P}.grupos.${clave}`)}`), [
      op(local('vih-solicitada', t(`${P}.vih.solicitada`)), v.solicitada),
      // El resultado solo existe si la prueba se solicitó.
      vihTieneResultado051(v) ? op(local('vih-resultado', t(`${P}.vih.resultado`)), v.resultado) : undefined,
      op(local('vih-tarv', t(`${P}.vih.tarv`)), v.tarv),
    ]);
  };
  vih('menor20', e.vihMenor20);
  vih('mayorIgual20', e.vihMayorIgual20);

  const hb = (clave: 'hbMenor20' | 'hbMayorIgual20', h: EstadoHb051): void => {
    const valor = valorNumerico(h.valor, U.gPorDl);
    if (!valor && !h.menor11) return;
    s.grupo(
      [LOINC_HB, local(clave === 'hbMenor20' ? 'hb-menor-20' : 'hb-mayor-igual-20', t(`${P}.${clave}`))],
      [cMarca(local('hb-menor-11', t(`${P}.hbMenor11`)), h.menor11)],
      valor ?? {}
    );
  };
  hb('hbMenor20', e.hbMenor20);
  hb('hbMayorIgual20', e.hbMayorIgual20);

  s.grupo(local('suplementos-prenatales', 'Suplementos prenatales'), [
    cSiNo(local('fe-indicado', t(`${P}.feIndicado`)), e.feIndicado),
    cSiNo(local('folatos-indicados', t(`${P}.folatosIndicados`)), e.folatosIndicados),
    cSiNo(local('calcio', t(`${P}.calcio`)), e.calcio),
    op(local('acido-acetilsalicilico', t(`${P}.acidoAcetilsalicilico`)), e.acidoAcetilsalicilico),
  ]);

  const sifilis = (clave: 'menor20' | 'mayorIgual20', f: EstadoSifilisFila051): void => {
    const semanas = t(`${P}.sifilis.semanas`);
    const fila = (k: 'noTreponemica' | 'treponemica' | 'tratamiento', valor: string, sem: string) => [
      op(local(`sifilis-${k}`, t(`${P}.sifilis.${k}`)), valor),
      // Las semanas solo tienen sentido con un resultado − o +.
      sifilisTieneSemanas051(valor as 'neg' | 'pos' | 'sd' | 'nc' | '') ? cNumero(local(`sifilis-${k}-semanas`, `${t(`${P}.sifilis.${k}`)} (${semanas})`), sem, U.semanas) : undefined,
    ];
    s.grupo(local(`sifilis-${clave === 'menor20' ? 'menor-20' : 'mayor-igual-20'}`, `${t(`${P}.sifilis.titulo`)} ${t(`${P}.grupos.${clave}`)}`), [
      ...fila('noTreponemica', f.noTreponemica, f.noTreponemicaSemanas),
      ...fila('treponemica', f.treponemica, f.treponemicaSemanas),
      ...fila('tratamiento', f.tratamiento, f.tratamientoSemanas),
      op(local('sifilis-tratamiento-pareja', t(`${P}.sifilis.tratamientoPareja`)), f.tratamientoPareja),
    ]);
  };
  sifilis('menor20', e.sifilisMenor20);
  sifilis('mayorIgual20', e.sifilisMayorIgual20);

  resultado(local('bacteriuria-menor-20', `${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.menor20`)}`), e.bacteriuriaMenor20);
  resultado(local('bacteriuria-mayor-igual-20', `${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.mayorIgual20`)}`), e.bacteriuriaMayorIgual20);

  const glucemia = (clave: 'menor20' | 'mayor30', g: EstadoGlucemia051): void => {
    const valor = valorNumerico(g.valor, U.mgPorDl);
    if (!valor && !g.mayorIgual92) return;
    s.grupo(
      [LOINC_GLUCEMIA_AYUNAS, local(clave === 'menor20' ? 'glucemia-menor-20' : 'glucemia-mayor-30', `${t(`${P}.glucemia.titulo`)} ${t(`${P}.glucemia.${clave}`)}`)],
      [cMarca(local('glucemia-mayor-igual-92', t(`${P}.glucemia.mayorIgual92`)), g.mayorIgual92)],
      valor ?? {}
    );
  };
  glucemia('menor20', e.glucemiaMenor20);
  glucemia('mayor30', e.glucemiaMayor30);

  s.grupo(local('educacion-plan-y-lactancia', 'Educación prenatal, plan de parto y lactancia'), [
    ...SESIONES_EDUCACION_PRENATAL_051.map((n) => cMarca(local(`educacion-prenatal-sesion-${n}`, `${t(`${P}.educacionPrenatal`)} — ${n}`), e.educacionPrenatal[n])),
    cSiNo(local('plan-parto-emergencia', t(`${P}.planPartoEmergencia`)), e.planPartoEmergencia),
    cSiNo(local('consejeria-lactancia-materna', t(`${P}.consejeriaLactanciaMaterna`)), e.consejeriaLactanciaMaterna),
  ]);

  s.cerrar();
}
